import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import {
  IconReceiptTax,
  IconX,
  IconCalendar,
  IconNotes,
  IconArrowUpRight,
  IconArrowDownRight,
  IconArrowsExchange,
  IconTag,
} from '@tabler/icons-react';

const getTipeBadge = (tipe) => {
  switch (tipe) {
    case 'Penjualan':
      return {
        label: 'Penjualan',
        className: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800',
        icon: <IconArrowUpRight size={14} className="text-emerald-500" />,
        isPlus: true,
      };
    case 'Pembelian':
      return {
        label: 'Pembelian',
        className: 'bg-rose-50 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300 border border-rose-200 dark:border-rose-800',
        icon: <IconArrowDownRight size={14} className="text-rose-500" />,
        isPlus: false,
      };
    case 'Setor_Tunai':
      return {
        label: 'Setor Tunai',
        className: 'bg-teal-50 text-teal-700 dark:bg-teal-950/50 dark:text-teal-300 border border-teal-200 dark:border-teal-800',
        icon: <IconArrowUpRight size={14} className="text-teal-500" />,
        isPlus: true,
      };
    case 'Tarik_Tunai':
      return {
        label: 'Tarik Tunai',
        className: 'bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300 border border-amber-200 dark:border-amber-800',
        icon: <IconArrowDownRight size={14} className="text-amber-500" />,
        isPlus: false,
      };
    case 'Refund_Penjualan':
      return {
        label: 'Return Penjualan',
        className: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800',
        icon: <IconArrowsExchange size={14} className="text-indigo-500" />,
        isPlus: false,
      };
    case 'Refund_Pembelian':
    case 'Return_Pembelian':
    case 'Return Pembelian':
    case 'RETURN PEMBELIAN':
      return {
        label: 'Return Pembelian',
        className: 'bg-orange-50 text-orange-700 dark:bg-orange-950/50 dark:text-orange-300 border border-orange-200 dark:border-orange-800',
        icon: <IconArrowsExchange size={14} className="text-orange-500" />,
        isPlus: true,
      };
    default:
      return {
        label: tipe || 'Transaksi',
        className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border border-slate-200 dark:border-slate-700',
        icon: <IconTag size={14} className="text-slate-500" />,
        isPlus: null,
      };
  }
};

export default function DetailSaldoKas({ closeModal, detailSaldoKas, isOpen }) {
  function numberWithCommas(x) {
    if (x === null || x === undefined || isNaN(x)) return '0';
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  const tipeInfo = getTipeBadge(detailSaldoKas?.tipe);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={closeModal}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 p-6 text-left align-middle shadow-2xl transition-all">
                
                {/* Header */}
                <div className="flex items-start justify-between pb-4 border-b border-slate-100 dark:border-slate-800">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-2xl bg-amber-500/10 dark:bg-amber-400/15 border border-amber-500/20 dark:border-amber-400/30 flex items-center justify-center text-amber-600 dark:text-amber-400 shadow-inner">
                      <IconReceiptTax size={22} />
                    </div>
                    <div>
                      <Dialog.Title as="h3" className="text-base sm:text-lg font-bold text-slate-800 dark:text-slate-100">
                        Detail Riwayat Saldo KAS
                      </Dialog.Title>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Rincian mutasi transaksi kas
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={closeModal}
                    type="button"
                    className="p-1.5 rounded-xl text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  >
                    <IconX size={18} />
                  </button>
                </div>

                {/* Body Details */}
                <div className="mt-5 space-y-4">
                  
                  {/* Highlight Box: Nominal & Saldo Akhir */}
                  <div className="p-4 rounded-2xl bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-transparent dark:from-amber-950/30 dark:via-slate-800/40 border border-amber-500/20 dark:border-amber-500/30">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 block mb-1">
                          Nominal Transaksi
                        </span>
                        <div className="text-lg sm:text-xl font-extrabold text-slate-800 dark:text-slate-100">
                          Rp {numberWithCommas(detailSaldoKas?.nominal ?? detailSaldoKas?.saldo_awal ?? 0)}
                        </div>
                      </div>
                      <div>
                        <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 block mb-1">
                          Saldo Akhir Kas
                        </span>
                        <div className="text-lg sm:text-xl font-extrabold text-amber-600 dark:text-amber-400">
                          Rp {numberWithCommas(detailSaldoKas?.saldo_akhir ?? 0)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Information List */}
                  <div className="bg-slate-50/70 dark:bg-slate-800/50 border border-slate-200/80 dark:border-slate-800 rounded-2xl divide-y divide-slate-100 dark:divide-slate-800 text-xs sm:text-sm">
                    {/* Kode Faktur */}
                    <div className="flex items-center justify-between p-3.5">
                      <span className="text-slate-500 dark:text-slate-400 font-medium">Kode Faktur</span>
                      <span className="font-mono font-bold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/60 px-2.5 py-1 rounded-lg">
                        {detailSaldoKas?.kode_trx || '-'}
                      </span>
                    </div>

                    {/* Tipe Transaksi */}
                    <div className="flex items-center justify-between p-3.5">
                      <span className="text-slate-500 dark:text-slate-400 font-medium">Tipe Transaksi</span>
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold ${tipeInfo.className}`}>
                        {tipeInfo.icon}
                        {tipeInfo.label}
                      </span>
                    </div>

                    {/* Tanggal Transaksi */}
                    <div className="flex items-center justify-between p-3.5">
                      <span className="text-slate-500 dark:text-slate-400 font-medium">Tanggal Transaksi</span>
                      <div className="flex items-center gap-1.5 text-slate-700 dark:text-slate-200 font-semibold">
                        <IconCalendar size={15} className="text-slate-400" />
                        <span>{detailSaldoKas?.tanggal || '-'}</span>
                      </div>
                    </div>

                    {/* Keterangan */}
                    <div className="p-3.5">
                      <div className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400 font-medium mb-1.5">
                        <IconNotes size={15} />
                        <span>Keterangan</span>
                      </div>
                      <div className="p-2.5 rounded-xl bg-white dark:bg-slate-900 border border-slate-200/70 dark:border-slate-800 text-slate-700 dark:text-slate-300 text-xs sm:text-sm leading-relaxed whitespace-pre-wrap">
                        {detailSaldoKas?.keterangan || <span className="italic text-slate-400">Tidak ada keterangan</span>}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer Actions */}
                <div className="mt-6 flex justify-end">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="w-full sm:w-auto px-6 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 font-bold text-xs sm:text-sm transition-all"
                  >
                    Tutup
                  </button>
                </div>

              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
